import { BellRing, CalendarCheck, Check, FileDown } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { BrowserFrame } from "@/components/blocks/frames/Frames";
import { EASE } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { VAERDICT_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const VAERDICT_HOST = new URL(VAERDICT_URL).host;
const NAV_KEYS = ["contacts", "files", "simulation", "alerts"] as const;
/** Value swaps and chips; screen-level changes use the 0.45 s step. */
const SWAP = 0.35;
const SCREEN = 0.45;

/** Vaerdict's own navigation: logo, then the app sections. */
function VaerdictNav({ active }: { active: "simulation" | "alerts" }) {
	const { dict } = useI18n();
	const nav = dict.scenes.vaerdict.nav;

	return (
		<div className="flex items-center gap-3 border-b px-3 py-2">
			<img
				src="/clients/vaerdict-logo.svg"
				alt=""
				width={231}
				height={74}
				className="h-5 w-auto shrink-0"
			/>
			<ul className="ml-auto flex gap-1 text-[10px] font-medium">
				{NAV_KEYS.map((key) => (
					<li
						key={key}
						className={cn(
							"rounded-full px-2.5 py-1",
							key === active
								? "bg-vd-orange text-background"
								: "text-muted-foreground",
							(key === "contacts" || key === "files") && "hidden sm:block",
						)}
					>
						{nav[key]}
					</li>
				))}
			</ul>
		</div>
	);
}

/** Swaps a value with a short vertical slide. */
function SwapValue({
	id,
	children,
	className,
}: {
	id: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<AnimatePresence mode="popLayout" initial={false}>
			<motion.span
				key={id}
				initial={{ opacity: 0, y: 6 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: -6 }}
				transition={{ duration: SWAP, ease: EASE }}
				className={cn("block", className)}
			>
				{children}
			</motion.span>
		</AnimatePresence>
	);
}

const ROWS = [
	{ key: "balance", tone: "bg-vd-teal", changes: false },
	{ key: "resale", tone: "bg-vd-rose", changes: true },
	{ key: "capital", tone: "bg-vd-orange", changes: true },
	{ key: "gain", tone: "bg-vd-yellow", changes: true },
	{ key: "irr", tone: "bg-vd-deep", changes: true },
] as const;

/** Reference study → resale price lowered → result recalculated, still positive. */
const SIMULATION_DURATIONS = [2600, 2200, 4600] as const;

/**
 * A client worries about a lower valuation: the adviser lowers the resale
 * price and the adjusted column shows the investment still comes out ahead.
 */
export function VaerdictSimulationScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const scene = dict.scenes.vaerdict;
	const { ref, index } = useSceneLoop(SIMULATION_DURATIONS, { initial: 2 });
	const editing = index === 1;
	const adjusted = index >= 1;
	const solved = index === 2;

	return (
		<div ref={ref} className={cn("w-full", className)}>
			<BrowserFrame host={VAERDICT_HOST} tag={dict.common.illustrationBadge}>
				<VaerdictNav active="simulation" />
				<div className="space-y-3 p-3 text-vd-ink sm:p-4">
					<div className="flex items-center justify-between gap-3">
						<div className="min-w-0">
							<p className="truncate text-[12px] font-semibold">
								{scene.client}
							</p>
							<p className="truncate text-[10px] text-muted-foreground">
								{scene.project}
							</p>
						</div>
						<span className="flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-medium">
							<FileDown className="size-3 text-vd-teal" />
							{scene.pdf}
						</span>
					</div>

					<div
						className={cn(
							"flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-[11px] transition-[border-color,box-shadow] duration-300",
							editing && "border-vd-rose ring-3 ring-vd-rose/15",
						)}
					>
						<span className="flex min-w-0 items-center gap-2">
							<span className="h-3 w-1 shrink-0 rounded-full bg-vd-rose" />
							<span className="truncate">{scene.field}</span>
						</span>
						<span className="flex items-center gap-1 font-mono font-medium tabular-nums">
							<SwapValue id={adjusted ? "after" : "before"}>
								{adjusted ? scene.fieldAfter : scene.fieldBefore}
							</SwapValue>
							<span
								className={cn(
									"h-3.5 w-px bg-vd-rose transition-opacity",
									editing ? "animate-pulse opacity-100" : "opacity-0",
								)}
							/>
						</span>
					</div>

					<div className="overflow-hidden rounded-lg border text-[11px]">
						<div className="grid grid-cols-[1fr_5.5rem_5.5rem] items-center bg-muted/60 px-3 py-1.5 font-mono text-[9px] tracking-[0.05em] text-muted-foreground uppercase sm:grid-cols-[1fr_6.5rem_6.5rem]">
							<span>{scene.horizon}</span>
							<span className="text-right">{scene.reference}</span>
							<span className="text-right">{scene.adjusted}</span>
						</div>
						<ul className="divide-y">
							{ROWS.map((row, rowIndex) => {
								const highlight = solved && row.changes;
								return (
									<li
										key={row.key}
										className="grid grid-cols-[1fr_5.5rem_5.5rem] items-center px-3 py-2 sm:grid-cols-[1fr_6.5rem_6.5rem]"
									>
										<span className="flex min-w-0 items-center gap-2">
											<span
												className={cn(
													"h-3 w-1 shrink-0 rounded-full",
													row.tone,
												)}
											/>
											<span className="truncate">{scene.rows[row.key]}</span>
										</span>
										<span className="text-right font-mono tabular-nums text-muted-foreground">
											{scene.values.reference[row.key]}
										</span>
										<span className="relative flex h-4 items-center justify-end font-mono tabular-nums">
											{index === 0 ? (
												<span className="text-muted-foreground">—</span>
											) : editing && row.changes ? (
												<motion.span
													className="block h-2 w-14 rounded-full bg-foreground/10"
													animate={{ opacity: [0.4, 1, 0.4] }}
													transition={{
														duration: 1.1,
														repeat: Number.POSITIVE_INFINITY,
														delay: rowIndex * 0.08,
													}}
												/>
											) : (
												<SwapValue
													id={`${row.key}-${index}`}
													className={cn(highlight && "font-semibold")}
												>
													{scene.values.adjusted[row.key]}
												</SwapValue>
											)}
										</span>
									</li>
								);
							})}
						</ul>
					</div>

					<motion.p
						initial={false}
						animate={{ opacity: solved ? 1 : 0, y: solved ? 0 : 6 }}
						transition={{ duration: SCREEN, ease: EASE }}
						className="flex items-center gap-2 rounded-lg bg-vd-teal/10 px-3 py-2 text-[11px] font-medium text-vd-deep"
					>
						<Check className="size-3.5 shrink-0" />
						<span className="min-w-0 text-pretty">{scene.verdict}</span>
					</motion.p>
				</div>
			</BrowserFrame>
		</div>
	);
}

const CLIENTS = [
	{ key: "a", tone: "bg-vd-teal" },
	{ key: "b", tone: "bg-vd-rose" },
	{ key: "c", tone: "bg-vd-orange" },
] as const;

/** Portfolio up to date → alerts raised → a meeting proposed. */
const FOLLOW_UP_DURATIONS = [2200, 3000, 4000] as const;

/** The client base turning yearly follow-up into meetings to propose. */
export function VaerdictFollowUpScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const followUp = dict.scenes.vaerdict.followUp;
	const { ref, index } = useSceneLoop(FOLLOW_UP_DURATIONS, { initial: 2 });
	const alerts = index >= 1;
	const booked = index === 2;

	return (
		<div ref={ref} className={cn("w-full", className)}>
			<BrowserFrame host={VAERDICT_HOST} tag={dict.common.illustrationBadge}>
				<VaerdictNav active="alerts" />
				<div className="p-3 text-vd-ink sm:p-4">
					<div className="mb-3 flex items-center justify-between gap-3">
						<div className="min-w-0">
							<p className="truncate text-[12px] font-semibold">
								{followUp.title}
							</p>
							<p className="truncate text-[10px] text-muted-foreground">
								{followUp.subtitle}
							</p>
						</div>
						<motion.span
							initial={false}
							animate={{ opacity: alerts ? 1 : 0, scale: alerts ? 1 : 0.9 }}
							transition={{ duration: SWAP, ease: EASE }}
							className="flex shrink-0 items-center gap-1.5 rounded-full bg-vd-ink px-2.5 py-1 text-[10px] font-semibold text-background"
						>
							<BellRing className="size-3 text-vd-yellow" />
							{followUp.count}
						</motion.span>
					</div>
					<ul className="space-y-2">
						{CLIENTS.map((client, clientIndex) => {
							const item = followUp.clients[client.key];
							const state =
								booked && client.key === "a"
									? "booked"
									: alerts
										? "alert"
										: "idle";
							return (
								<li
									key={client.key}
									className="flex items-center gap-3 rounded-xl border px-3 py-2.5 text-[11px]"
								>
									<span
										className={cn(
											"grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-semibold text-background",
											client.tone,
										)}
									>
										{item.initials}
									</span>
									<span className="min-w-0 flex-1">
										<span className="block truncate font-medium">
											{item.name}
										</span>
										<span className="block truncate text-[10px] text-muted-foreground">
											{item.project}
										</span>
									</span>
									<span className="flex max-w-[52%] justify-end">
										<AnimatePresence mode="popLayout" initial={false}>
											<motion.span
												key={state}
												initial={{ opacity: 0, x: 8 }}
												animate={{ opacity: 1, x: 0 }}
												exit={{ opacity: 0, x: -8 }}
												transition={{
													duration: SWAP,
													ease: EASE,
													delay: state === "alert" ? clientIndex * 0.18 : 0,
												}}
												className={cn(
													"flex min-w-0 items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-medium",
													state === "booked" && "bg-vd-deep text-background",
													state === "alert" && "border bg-background",
													state === "idle" && "text-muted-foreground",
												)}
											>
												{state === "booked" ? (
													<CalendarCheck className="size-3 shrink-0" />
												) : state === "alert" ? (
													<span
														className={cn(
															"size-1.5 shrink-0 rounded-full",
															client.tone,
														)}
													/>
												) : null}
												<span className="truncate">
													{state === "booked"
														? followUp.booked
														: state === "alert"
															? item.alert
															: followUp.upToDate}
												</span>
											</motion.span>
										</AnimatePresence>
									</span>
								</li>
							);
						})}
					</ul>
				</div>
			</BrowserFrame>
		</div>
	);
}
